import React, { useState, useEffect } from "react";
import styles from "./TrainingsPage.module.scss";
import ProductTutorials from "../../components/ProductTutorials";
import PlatformTutorials from "../../components/PlatformTutorials";
import {
  SALESPERSON_PLATFORM_TUTORIALS,
  SALESPERSON_PRODUCT_TUTORIALS,
} from "../../../../constants/index";
import Button from "../../../../components/EcommerceActionButton/Button";
import {
  useCompleteTraining,
  useGetSalespersonProfile,
} from "../../../../utils/react-query/querySalesperson";
import { warningToast } from "../../../../services/toasts";

const TrainingsPage: React.FC = () => {
  const [watchedVideos, setWatchedVideos] = useState<Set<string>>(new Set());
  const { mutateAsync: completeTrainingMutation, isLoading: isSubmitting } =
    useCompleteTraining();
  const { data: salespersonData } = useGetSalespersonProfile();

  const totalVideos =
    SALESPERSON_PLATFORM_TUTORIALS.length +
    SALESPERSON_PRODUCT_TUTORIALS.length;

  // Load watched videos from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("watched_training_videos");
    if (stored) {
      setWatchedVideos(new Set(JSON.parse(stored)));
    }
  }, []);

  // Save watched videos to localStorage
  const handleVideoWatched = (videoId: string) => {
    setWatchedVideos((prev) => {
      const newWatchedVideos = new Set(prev);
      newWatchedVideos.add(videoId);
      localStorage.setItem(
        "watched_training_videos",
        JSON.stringify(Array.from(newWatchedVideos))
      );
      return newWatchedVideos;
    });
  };

  const allVideosWatched = watchedVideos.size >= totalVideos;
  const isTrainingAlreadyFinished =
    salespersonData?.is_training_finished === true;

  const handleCompleteTraining = async () => {
    if (!allVideosWatched) {
      warningToast(
        "Debes ver todos los videos antes de finalizar las capacitaciones"
      );
      return;
    }

    await completeTrainingMutation();
  };

  return (
    <div className={styles.trainingsContainer}>
      <div className={styles.header}>
        <h1 className={styles.title}>Capacitaciones</h1>
        <p className={styles.subtitle}>
          Completa todos los tutoriales para finalizar tu capacitación
        </p>
        <div className={styles.progress}>
          <span className={styles.progressText}>
            Progreso: {watchedVideos.size} / {totalVideos} videos vistos
          </span>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${(watchedVideos.size / totalVideos) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className={styles.tutorialsGrid}>
        <div className={styles.tutorialColumn}>
          <PlatformTutorials
            onVideoWatched={handleVideoWatched}
            watchedVideos={watchedVideos}
          />
        </div>
        <div className={styles.tutorialColumn}>
          <ProductTutorials
            onVideoWatched={handleVideoWatched}
            watchedVideos={watchedVideos}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          color="primary"
          size="big"
          disabled={
            !allVideosWatched || isSubmitting || isTrainingAlreadyFinished
          }
          onClick={handleCompleteTraining}
          className={styles.completeButton}
          isLoading={isSubmitting}
        >
          {isTrainingAlreadyFinished
            ? "Capacitaciones Finalizadas"
            : "Finalizar Capacitaciones"}
        </Button>
        {!allVideosWatched && !isTrainingAlreadyFinished && (
          <p className={styles.helperText}>
            Debes ver todos los videos para poder finalizar las capacitaciones
          </p>
        )}
        {isTrainingAlreadyFinished && (
          <p className={styles.helperText}>
            Ya has completado las capacitaciones
          </p>
        )}
      </div>
    </div>
  );
};

export default TrainingsPage;
